/**
 * The case detail panel — one slide-out, shared by every book's card.
 *
 * Brings the Case Portal's detail pane into Command Center: the operator clicks
 * a matter and reads its full picture in place. LF-admin only (no HF/OF
 * masking — the operator sees everything; ADR-0068). See `@/lib/case-detail`
 * for the contract and the per-book mappers that feed it.
 *
 * **Slice 4 (portal→CC merge, ADR-0067)** upgraded the chrome and added the
 * net-new sections:
 *   - **Chrome:** 580px right-side slide-over with a navy backdrop, slide-in
 *     transition, click-outside + Escape dismissal, and proper modal aria.
 *     Replaces the slice-1 plain `<aside max-w-md>` no-backdrop pop.
 *   - **New sections:** next filing deadline (date + type, with an overdue cue),
 *     FSA submission status (derived per book), unified timeline (routing
 *     milestones merged with monthly-update entries, newest first), plus the
 *     existing "Case file" renamed to "Documents" — its content unchanged but
 *     the heading matches the merge spec.
 *
 * Each section still renders only when its data is present, so a thin terminal
 * card yields a thin panel and a rich circulating lead a rich one — the blanks
 * remain the map of the data work still to do.
 */

import { useEffect, useLayoutEffect, useState, type ReactNode } from "react";
import { X } from "lucide-react";
import type {
  CaseDetail,
  CaseDetailFsa,
  CaseDetailSettlement,
  CaseDetailTimelineEvent,
} from "@/lib/case-detail";
import { dateInPT, todayInPT } from "@command-center/shared";
import { weaveNotesIntoTimeline } from "@/lib/case-detail";
import { dayZone } from "@/lib/format";
import { usePins } from "@/lib/use-surface";
import { Badge } from "@/components/ui/badge";
import { CaseDetailActionFooter } from "@/components/case-detail-action-footer";
import { CaseMessages } from "@/components/case-messages";
import { CaseNoteComposer } from "@/components/case-note-composer";
import { useCaseNotes } from "@/lib/use-case-notes";
import { CaseQuestions } from "@/components/case-questions";
import { PinToggle } from "@/components/pin-toggle";

/** Whole-dollar USD — settlements read as round figures. */
function usd(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

/** Fee share decimal → percent label (0.6 → "60%"). */
function pct(d: number): string {
  return `${Math.round(d * 100)}%`;
}

/** Compact date — "May 12". Routing legs span weeks, not years. The day
 *  comes from `dayZone` (a date-only value is its own day, an instant its
 *  Pacific day); an unparseable input renders as itself. */
function fmtDate(iso: string): string {
  const z = dayZone(iso);
  if (z === null) return iso;
  return new Date(z.ms).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: z.timeZone,
  });
}

/** Compact date with year — for the timeline + filing deadline, which span
 *  years (a SOL can be far out). Same day rule as fmtDate above. */
function fmtDateYear(iso: string): string {
  const z = dayZone(iso);
  if (z === null) return iso;
  return new Date(z.ms).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: z.timeZone,
  });
}

/** Whole days from today's Pacific day to a date's day — positive = future,
 *  negative = past. A date-only value is its own day; an instant counts from
 *  its Pacific day. */
function daysUntil(iso: string): number {
  const day = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : dateInPT(iso);
  if (day === null) return 0;
  const target = Date.parse(`${day}T00:00:00Z`);
  if (Number.isNaN(target)) return 0;
  return Math.round((target - Date.parse(`${todayInPT()}T00:00:00Z`)) / 86_400_000);
}

/** A titled block with the panel's section chrome. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-t px-5 py-4 first:border-t-0">
      <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      {children}
    </section>
  );
}

export function CaseDetailPanel({
  detail,
  onClose,
  testId = "case-detail-panel",
}: {
  detail: CaseDetail;
  onClose: () => void;
  /** Per-book testid so the existing book tests keep asserting their own panel. */
  testId?: string;
}) {
  const chips = [detail.caseType, detail.incidentState, detail.originatingFirmName]
    .filter((v): v is string => typeof v === "string" && v.length > 0)
    .join("  ·  ");
  const hasContact = Boolean(detail.phone || detail.email);
  const caseFile = detail.caseFile ?? [];
  const routing = detail.routing ?? [];
  const rawTimeline = detail.timeline ?? [];

  // Case Notes (#692) — the LF-internal record of work on this matter, woven
  // into the timeline rather than parked in a section of their own: a case has
  // one history, and the system's events and the operator's account of them
  // only answer "what happened here" together. Fetched matter-scoped, so the
  // notes of every placement the case has held appear, declined ones included.
  const notesLeadId = detail.leadRecordId ?? null;
  const notesQuery = useCaseNotes(notesLeadId ?? "", notesLeadId !== null);
  const noteTargets = notesQuery.data?.targets ?? [];
  const firmNameFor = (placementId: string) =>
    noteTargets.find((t) => t.placementId === placementId)?.firmName ?? null;
  const timeline = weaveNotesIntoTimeline(
    rawTimeline,
    notesQuery.data?.notes ?? [],
    firmNameFor,
  );

  // #541: pin a browsed case — the same one-click matter watch as the belt
  // rows (shared PinToggle), from any panel that knows its lead (global
  // search, the books). Pin state reads the shared ["surface"] cache
  // (unpolled here); the mutations invalidate it, so the Surface's Pinned
  // tab stays coherent.
  const pinLeadId = detail.leadActionTarget?.leadId ?? null;
  const { pins } = usePins(pinLeadId !== null);

  // Slide-in animation: start off-screen on mount, flip to in-place after
  // first paint so the browser interpolates the transform. Avoids the
  // jarring "appears already open" of an instant mount, without a portal
  // library (which would also handle the unmount slide-out we don't do
  // this slice — the parent removes the panel on close).
  const [mounted, setMounted] = useState(false);
  useLayoutEffect(() => {
    // Run after the first paint by queuing in the next animation frame —
    // useLayoutEffect that simply sets state synchronously is committed
    // alongside the initial render, so the transition never sees the
    // off-screen state.
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Escape to dismiss. The handler hangs off `document` so it fires even
  // when focus is inside an input — the operator typing a decline reason
  // and hitting Escape expects "close the panel."
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Click-outside via the backdrop. The panel `<aside>` itself stops
  // propagation so a click inside doesn't bubble up to the wrapper and
  // dismiss.
  const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex"
      role="presentation"
      onMouseDown={onBackdropClick}
      data-testid={`${testId}-overlay`}
    >
      {/* Navy backdrop — the portal idiom. Soft, not jet-black: the panel is
          the focus, the backdrop just dims the chrome. */}
      <div
        aria-hidden="true"
        className={`absolute inset-0 bg-[#000527] transition-opacity duration-200 ${
          mounted ? "opacity-40" : "opacity-0"
        }`}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`Case detail for ${detail.name}`}
        className={`relative ml-auto flex h-full w-full max-w-[580px] flex-col border-l bg-card text-card-foreground shadow-xl transition-transform duration-200 ${
          mounted ? "translate-x-0" : "translate-x-full"
        }`}
        data-testid={testId}
        onMouseDown={(e) => e.stopPropagation()}
      >
      <header className="flex items-start justify-between border-b px-5 py-4">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-serif text-xl leading-tight">
              {detail.name}
            </span>
            {detail.signed ? <Badge variant="blue">Transfer</Badge> : null}
            {detail.status ? <Badge variant="muted">{detail.status}</Badge> : null}
          </div>
          {chips ? (
            <span className="text-xs text-muted-foreground">{chips}</span>
          ) : null}
          {detail.handlingFirmName ? (
            <span className="text-xs text-muted-foreground">
              → {detail.handlingFirmName}
            </span>
          ) : null}
          {detail.receivedDate ? (
            <span
              className="text-xs text-muted-foreground"
              data-testid={`${testId}-received`}
            >
              Received {fmtDateYear(detail.receivedDate)}
            </span>
          ) : null}
        </div>
        <div className="flex items-center gap-1">
          <PinToggle leadId={pinLeadId} pins={pins} testId={`${testId}-pin`} />
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            aria-label="Close case detail"
            data-testid={`${testId}-close`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        {/* Transfer advisory — a Transfer is a fact (already-retained client),
            so this is a neutral band, never a coloured alert (COLOR-MODEL (evicted 2026-08-29, #775 round 10) §5:
            Transfer = neutral). Ports the portal's advisory information; drops
            its HF-facing "don't contact the client" note (the CC viewer is the
            LF operator, not the firm). */}
        {detail.signed ? (
          <div
            className="border-b border-l-4 border-l-muted-foreground/30 bg-muted/50 px-5 py-3 text-sm text-foreground"
            data-testid={`${testId}-transfer-banner`}
          >
            <span className="font-medium">Transfer.</span>{" "}
            {detail.originatingFirmName
              ? `The client is already represented by ${detail.originatingFirmName}.`
              : "The client is already represented by the originating firm."}
          </div>
        ) : null}

        {hasContact ? (
          <Section title="Client contact">
            <dl className="flex flex-col gap-1 text-sm">
              {detail.phone ? (
                <div className="flex gap-2">
                  <dt className="w-14 shrink-0 text-muted-foreground">Phone</dt>
                  <dd>{detail.phone}</dd>
                </div>
              ) : null}
              {detail.email ? (
                <div className="flex gap-2">
                  <dt className="w-14 shrink-0 text-muted-foreground">Email</dt>
                  <dd className="break-all">{detail.email}</dd>
                </div>
              ) : null}
            </dl>
          </Section>
        ) : null}

        <Section title="Case summary">
          {detail.caseSummary ? (
            <p className="whitespace-pre-wrap text-sm text-foreground">
              {detail.caseSummary}
            </p>
          ) : (
            <p className="text-sm italic text-muted-foreground">
              No case summary on this matter.
            </p>
          )}
        </Section>

        {/* Firm messages (#542) — the matter's Portal Messages threads, read
            through the CC worker proxy. Distinct from a pin's operator Notes
            (LF-internal); these are firm-facing conversations. Renders
            whenever the panel knows its lead — the always-shown posture
            (like Timeline) so operators learn firm comms live here. */}
        {detail.leadRecordId ? (
          <Section title="Messages">
            <CaseMessages leadId={detail.leadRecordId} testId={`${testId}-messages`} />
          </Section>
        ) : null}

        {/* Case questions (#613) — the firm↔firm channel. LF co-asks the
            handling firm from here (the only ask door), gated Placed-and-beyond
            on the active placement's decision. Shown whenever the matter is
            known (like Messages), so operators learn the channel lives here. */}
        {detail.leadRecordId ? (
          <Section title="Case questions">
            <CaseQuestions
              leadId={detail.leadRecordId}
              placementId={detail.actionTarget?.placementId ?? null}
              decision={detail.actionTarget?.decision ?? null}
              clientName={detail.name}
              testId={`${testId}-questions`}
            />
          </Section>
        ) : null}

        {/* Filing deadline: always shown for a Placed matter (an active
            placement carries this obligation), with an empty state when none
            is on file yet — mirroring the portal, which shows the section for
            every HF status rather than hiding it. Pre-placement books have no
            placement, so the field is N/A and the section stays hidden. */}
        {detail.nextFilingDeadline || detail.stage === "Placed" ? (
          <Section title="Next filing deadline">
            {detail.nextFilingDeadline ? (
              <FilingDeadlineBlock
                date={detail.nextFilingDeadline}
                type={detail.nextFilingDeadlineType ?? null}
                testId={testId}
              />
            ) : (
              <p
                className="text-sm italic text-muted-foreground"
                data-testid={`${testId}-filing-empty`}
              >
                No filing deadline on file yet.
              </p>
            )}
          </Section>
        ) : null}

        {detail.fsa ? (
          <Section title="FSA">
            <FsaBlock fsa={detail.fsa} testId={testId} />
          </Section>
        ) : null}

        {caseFile.length > 0 ? (
          <Section title="Documents">
            <ul className="flex flex-col gap-1 text-sm">
              {caseFile.map((doc) => (
                <li key={doc.id} className="flex items-baseline gap-2">
                  <a
                    href={doc.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-interactive hover:underline"
                  >
                    {doc.filename}
                  </a>
                  <span className="text-xs text-muted-foreground">
                    {Math.round(doc.size / 1024)} KB
                  </span>
                </li>
              ))}
            </ul>
          </Section>
        ) : null}

        {detail.declineReason ? (
          <Section title="Decline reason">
            <p className="whitespace-pre-wrap text-sm text-foreground">
              {detail.declineReason}
            </p>
          </Section>
        ) : null}

        {detail.dropReason ? (
          <Section title="Drop reason">
            <p className="whitespace-pre-wrap text-sm text-foreground">
              {detail.dropReason}
            </p>
          </Section>
        ) : null}

        {detail.settlement ? (
          <Section title="Settlement & disbursement">
            <SettlementBlock settlement={detail.settlement} detail={detail} />
          </Section>
        ) : null}

        {/* Timeline — always shown (parity with the portal), led by a
            stage-aware prompt so the operator reads where the matter stands
            and what to expect. Neutral throughout: the connecting rule and
            dots carry no hue (a sequence is a fact; ADR-0132), and the prompt
            is muted, never the portal's orange/blue gradient (colour is earned
            by a call to action, ADR-0134). The routing-history fallback (for
            the lead-only global-search lookup) is preserved under the same
            heading — routing IS the timeline for those. */}
        <Section title="Timeline">
          <TimelinePrompt stage={detail.stage ?? null} testId={testId} />
          {timeline.length > 0 ? (
            <TimelineBlock events={timeline} testId={testId} />
          ) : routing.length > 0 ? (
            <ol className="flex flex-col gap-2 text-sm">
              {routing.map((leg, i) => (
                <li key={i} className="flex flex-col gap-0.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">
                      {leg.firmName ?? "Unknown firm"}
                    </span>
                    {leg.decision ? (
                      <span className="text-xs text-muted-foreground">
                        {leg.decision}
                      </span>
                    ) : null}
                    {leg.dateSent ? (
                      <span className="text-xs text-muted-foreground">
                        · sent {fmtDate(leg.dateSent)}
                      </span>
                    ) : null}
                  </div>
                  {leg.declineReason ? (
                    <span className="text-xs text-error">
                      {leg.declineReason}
                    </span>
                  ) : null}
                </li>
              ))}
            </ol>
          ) : (
            <p
              className="text-sm italic text-muted-foreground"
              data-testid={`${testId}-timeline-empty`}
            >
              No timeline events yet.
            </p>
          )}
          {/* The write affordance sits under the timeline it writes to, so the
              operator sees the story before adding to it. Only where the matter
              is known — an unrouted lead has no placement to anchor against. */}
          {notesLeadId !== null ? (
            <div className="mt-4 border-t border-border pt-3">
              <CaseNoteComposer
                leadId={notesLeadId}
                targets={noteTargets}
                testId={`${testId}-note-composer`}
              />
            </div>
          ) : null}
        </Section>
      </div>

      {detail.actionTarget || detail.leadActionTarget ? (
        <CaseDetailActionFooter
          target={detail.actionTarget ?? null}
          leadTarget={detail.leadActionTarget ?? null}
          handlingFirmName={detail.handlingFirmName ?? null}
          onClose={onClose}
          testId={testId}
        />
      ) : null}
      </aside>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section blocks
// ---------------------------------------------------------------------------

/** Next filing deadline — date + type, with an overdue cue.  */
function FilingDeadlineBlock({
  date,
  type,
  testId,
}: {
  date: string;
  type: string | null;
  testId: string;
}) {
  const days = daysUntil(date);
  // Cue thresholds: past → red (overdue), ≤ 30d → amber (the gate the
  // portal uses), else muted. The numbers come from the portal's
  // Monthly Update modal logic ("required when within 30 days").
  const tone =
    days < 0
      ? "text-error"
      : days <= 30
      ? "text-warning"
      : "text-foreground";
  const label =
    days < 0
      ? `${Math.abs(days)}d overdue`
      : days === 0
      ? "today"
      : `in ${days}d`;
  return (
    <div className="flex flex-col gap-1 text-sm">
      <div className={`flex items-baseline gap-2 ${tone}`}>
        <span className="font-semibold tabular-nums" data-testid={`${testId}-filing-date`}>
          {fmtDateYear(date)}
        </span>
        <span className="text-xs">({label})</span>
      </div>
      {type ? (
        <span className="text-xs text-muted-foreground" data-testid={`${testId}-filing-type`}>
          {type}
        </span>
      ) : null}
    </div>
  );
}

/** FSA status — outstanding (with the wait clock) or submitted — plus the
 *  client-signed FSA document (filename + download) when it's on file. The
 *  download is a read-affordance the scope guard keeps in CC; the upload is a
 *  firm-native write that stays in the portal. */
function FsaBlock({ fsa, testId }: { fsa: CaseDetailFsa; testId: string }) {
  return (
    <div className="flex flex-col gap-2 text-sm" data-testid={`${testId}-fsa`}>
      {fsa.status === "outstanding" ? (
        <div className="flex items-center gap-2">
          <span className="rounded bg-warning-bg px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-warning">
            Outstanding
          </span>
          {fsa.date ? (
            <span className="text-xs text-muted-foreground">
              since {fmtDateYear(fsa.date)}
            </span>
          ) : null}
        </div>
      ) : (
        <div className="flex items-center gap-2">
          <span className="rounded bg-success-bg px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-success">
            Submitted
          </span>
          {fsa.date ? (
            <span className="text-xs text-muted-foreground">
              {fmtDateYear(fsa.date)}
            </span>
          ) : null}
        </div>
      )}
      {fsa.document ? (
        <a
          href={fsa.document.url}
          target="_blank"
          rel="noreferrer"
          className="text-interactive hover:underline"
          data-testid={`${testId}-fsa-doc`}
        >
          {fsa.document.filename}
        </a>
      ) : null}
    </div>
  );
}

/** The stage-aware prompt above the timeline — operator-voiced, telling the
 *  reader where the matter stands and what to expect next. Neutral by law
 *  (ADR-0134): the portal renders this over an orange/blue gradient, which is
 *  decorative colour CC doesn't spend; here it's a muted band. Returns null for
 *  limbo / unknown stages, where the events (or the empty state) speak for
 *  themselves. */
function stagePromptText(stage: string | null): string | null {
  switch (stage) {
    case "Under Review":
      return "Under review — the handling firm is evaluating. Its outreach and decision post here.";
    case "Accepted":
      return "Accepted — the firm is working to sign the client. Outreach posts here until the signed FSA comes back.";
    case "Placed":
      return "Placed — expect a monthly update from the handling firm. Case activity posts here.";
    case "Settled":
      return "Settled — awaiting disbursement. Settlement and payment milestones post here.";
    case "Case Closed":
      return "Case closed — all payments confirmed. Full history below.";
    case "Dropped":
      return "Case dropped — the handling firm ended representation. Full history below.";
    case "Rejected":
      return "Rejected — no firm took the matter. Routing history below.";
    default:
      return null;
  }
}

function TimelinePrompt({
  stage,
  testId,
}: {
  stage: string | null;
  testId: string;
}) {
  const text = stagePromptText(stage);
  if (text === null) return null;
  return (
    <p
      className="mb-3 rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground"
      data-testid={`${testId}-timeline-prompt`}
    >
      {text}
    </p>
  );
}

/** The merged timeline — routing events + monthly-update entries, newest first.
 *  A small neutral dot marks each event and a single continuous neutral rule
 *  connects them (a sequence is a fact — the dot and rule carry no hue per
 *  ADR-0132). The entry label names its kind (routing vs monthly-update). */
function TimelineBlock({
  events,
  testId,
}: {
  events: CaseDetailTimelineEvent[];
  testId: string;
}) {
  return (
    <ol
      className="relative flex flex-col gap-3 text-sm"
      data-testid={`${testId}-timeline`}
    >
      {/* One continuous connecting rule behind the dots, first to last. Each
          dot carries a card-coloured ring so the rule reads as broken at every
          node (the portal's white-filled-circle idiom, neutralised). The
          `left-[3.5px]` offset centres the 1px rule under the 8px (`h-2 w-2`)
          dot — keep the two in sync if the dot size changes. */}
      {events.length > 1 ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute bottom-2 left-[3.5px] top-2 w-px bg-border"
        />
      ) : null}
      {events.map((e, i) => (
        <li key={i} className="relative flex gap-3">
          <span
            className="relative z-10 mt-1.5 h-2 w-2 shrink-0 rounded-full bg-foreground ring-2 ring-card"
            aria-hidden="true"
          />
          <div className="flex flex-1 flex-col gap-0.5">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-medium">{e.label}</span>
              <span className="text-xs text-muted-foreground tabular-nums">
                {fmtDateYear(e.date)}
              </span>
            </div>
            {e.detail ? (
              <span className="text-xs text-muted-foreground whitespace-pre-wrap">
                {e.detail}
              </span>
            ) : null}
          </div>
        </li>
      ))}
    </ol>
  );
}

/** The fee split + settlement amount. Drops the LF line when LegalFlare is the
 *  OF (two-party split). Shows the amount even when no split is on the card. */
function SettlementBlock({
  settlement: s,
  detail,
}: {
  settlement: CaseDetailSettlement;
  detail: CaseDetail;
}) {
  const parties: { role: string; name: string | null; pct: number | null }[] = [
    { role: "HF", name: detail.handlingFirmName ?? null, pct: s.hfFeePct },
  ];
  if (!s.isLegalFlareOf) {
    parties.push({ role: "LF", name: "LegalFlare", pct: s.lfFeePct });
  }
  parties.push({ role: "OF", name: detail.originatingFirmName ?? null, pct: s.ofFeePct });
  const hasSplit = parties.some((p) => p.pct !== null);

  return (
    <div className="flex flex-col gap-2 text-sm">
      {s.amount !== null ? (
        <div className="text-lg font-semibold tabular-nums text-success">
          {usd(s.amount)}
        </div>
      ) : (
        <div className="text-sm italic text-muted-foreground">
          Amount not reported
        </div>
      )}
      {hasSplit ? (
        <ul className="flex flex-col gap-1">
          {parties.map((p) => (
            <li key={p.role} className="flex items-center gap-2">
              <span className="rounded bg-muted px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {p.role}
              </span>
              <span>{p.name ?? "—"}</span>
              {p.pct !== null ? (
                <span className="ml-auto tabular-nums text-muted-foreground">
                  {pct(p.pct)}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
